import React from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import OpeningsAndPlacementsWidget from '../OpeningsAndPlacementsWidget';
import PotentialRevenueWidget from '../PotentialRevenueWidget';
import WorkerStatusWidget from '../WorkerStatusWidget';

import dashboardHelpers from './dashboardHelpers';

/**
 * Shared dashboard widgets component
 * Renders the standard three-widget layout used across all dashboard views
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether widgets are in loading state
 * @param {Object} props.workers - Worker data for WorkerStatusWidget
 * @param {Object} props.revenue - Revenue data for PotentialRevenueWidget
 * @param {Object} props.revenueLabels - Labels for revenue widget
 * @param {Object} props.futurePlacementsData - Data for OpeningsAndPlacementsWidget
 * @param {boolean} [props.showWidgets=true] - Whether to show the widgets (for conditional rendering)
 */
const DashboardWidgets = ({
  isLoading,
  workers,
  revenue,
  revenueLabels,
  futurePlacementsData,
  showWidgets = true,
}) => {
  if (!showWidgets) {
    return null;
  }

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={12} xl={4}>
        <WorkerStatusWidget
          isLoading={isLoading}
          activeWorkers={workers?.active || 0}
          pendingOnboarding={workers?.pendingOnboarding || 0}
          waitingVerification={workers?.waitingVerification || 0}
          atRisk={workers?.atRisk || 0}
          total={workers?.total || 0}
        />
      </Grid>

      <Grid item xs={12} lg={6} xl={4}>
        <PotentialRevenueWidget
          labels={revenueLabels}
          availableAmount={Math.round(revenue?.unscheduled) || 0}
          totalEarnedAmount={Math.round(revenue?.earnedTrailing12Month) || 0}
          scheduledAmount={Math.round(revenue?.scheduled) || 0}
        />
      </Grid>

      <Grid item xs={12} lg={6} xl={4}>
        <OpeningsAndPlacementsWidget
          isLoading={isLoading}
          totalOpenings={futurePlacementsData?.totalOpenings || 0}
          totalActivePlacements={futurePlacementsData?.totalActivePlacements || 0}
          percentStaffed={dashboardHelpers.getFillRate(
            futurePlacementsData?.totalActivePlacements,
            futurePlacementsData?.totalOpenings
          )}
          totalWorkerCancellations={futurePlacementsData?.totalWorkerCancellations || 0}
          cancellationToPlacementRatio={futurePlacementsData?.cancellationToPlacementRatio || 0}
        />
      </Grid>
    </Grid>
  );
};

DashboardWidgets.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  workers: PropTypes.shape({
    active: PropTypes.number,
    pendingOnboarding: PropTypes.number,
    waitingVerification: PropTypes.number,
    atRisk: PropTypes.number,
    total: PropTypes.number,
  }),
  revenue: PropTypes.shape({
    unscheduled: PropTypes.number,
    earnedTrailing12Month: PropTypes.number,
    scheduled: PropTypes.number,
  }),
  revenueLabels: PropTypes.shape({
    title: PropTypes.string,
    totalRevenue: PropTypes.string,
    scheduled: PropTypes.string,
  }),
  futurePlacementsData: PropTypes.shape({
    totalOpenings: PropTypes.number,
    totalActivePlacements: PropTypes.number,
    totalWorkerCancellations: PropTypes.number,
    cancellationToPlacementRatio: PropTypes.number,
  }),
  showWidgets: PropTypes.bool,
};

export default DashboardWidgets;
